import type { OrderDetailsCharge, OrderDetailsStatusSemantic, OrderDetailsUI } from "@/domain/order/order-details.model"
import { roundMoney } from "@/domain/money/round-money"
import { classifyCharge } from "@/domain/order/charge-classification"
import { canRetryPayment } from "@/domain/order/order-details-retry"
import { buildOrderDetailsTimeline } from "@/domain/order/order-details-timeline"
import { mapSavingsSummary } from "@/domain/billing/billing-preview.adapter"
import {
  resolveOrderChannelLabel,
  resolveOrderFulfillmentLabel,
} from "@/domain/order/order-fulfillment-labels"
import { OrderStatus, OrderStatusLabel, parseOrderStatus } from "@/types/enums/OrderStatus"
import { PaymentTiming, PaymentTimingLabel, parsePaymentTiming } from "@/types/enums/PaymentTiming"
import { paymentTimingToCode, resolvePaymentTimingFromOrder } from "@/domain/order/order-payment-timing"
import { resolvePaymentMethodDisplayLabel } from "@/domain/payment/payment-method-display"
import { PICKUP_ORDER_TYPE } from "@/domain/checkout/fulfilment"

function titleCasePaymentType(raw: string): string {
  const s = raw.trim()
  if (!s) return ""
  return s
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

type JsonObject = Record<string, unknown>

function isRecord(v: unknown): v is JsonObject {
  return v != null && typeof v === "object" && !Array.isArray(v)
}

function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function str(v: unknown): string {
  if (v == null) return ""
  return String(v).trim()
}

function pickArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : []
}

/** Non‑negative monetary amount, rounded to cents. */
function moneyFromUnknown(v: unknown): number {
  return roundMoney(Math.max(0, num(v)))
}

/** Present numeric money, or null when the API omitted the field. Never coerces a missing amount to 0. */
function optionalMoney(v: unknown): number | null {
  if (v == null || v === "") return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  return roundMoney(Math.max(0, n))
}

function mapPaymentStatusUxLabel(upper: string): string {
  const u = upper.toUpperCase().replace(/\s+/g, "_")
  if (!u || u === "UNKNOWN") return "Unknown"
  if (/(^|_)SUCCESS($|_)|CAPTURED|PAID|SETTLED|SUCCEEDED|COMPLETED/i.test(u)) return "Paid"
  if (/FAILED|FAILURE|DECLINED|ERROR|REJECTED/i.test(u)) return "Payment Failed"
  if (/PENDING|AUTHORIZED|CREATED|PROCESSING|REQUIRES|AWAITING/i.test(u)) return "Awaiting Payment"
  return titleCasePaymentType(u.replace(/_/g, " "))
}

/**
 * Semantic order status for production UI (chips, hero accent, analytics stage).
 * Customer labels come from {@link OrderStatusLabel}. Analytics `stage` keys are unchanged.
 */
export function mapOrderStatusSemantic(
  status: unknown,
  fallbackLabel: string,
  fallbackCode: string
): OrderDetailsStatusSemantic {
  let token = ""
  if (isRecord(status)) {
    token = str(status.code) || str(status.name) || str(status.label) || str(status.value) || str(status.id)
  } else if (status != null) {
    token = str(status)
  }
  const t = token.replace(/\s+/g, "").toLowerCase()
  const fb = fallbackLabel || fallbackCode || "Unknown"

  const parsed = parseOrderStatus(status)
  const semanticByStatus: Record<OrderStatus, OrderDetailsStatusSemantic> = {
    [OrderStatus.Unknown]: { label: OrderStatusLabel[OrderStatus.Unknown], color: "gray", stage: "UNKNOWN" },
    [OrderStatus.Pending]: { label: OrderStatusLabel[OrderStatus.Pending], color: "yellow", stage: "PLACED" },
    [OrderStatus.Confirmed]: { label: OrderStatusLabel[OrderStatus.Confirmed], color: "blue", stage: "CONFIRMED" },
    [OrderStatus.Preparing]: { label: OrderStatusLabel[OrderStatus.Preparing], color: "orange", stage: "PREPARING" },
    [OrderStatus.Packaging]: { label: OrderStatusLabel[OrderStatus.Packaging], color: "orange", stage: "PREPARING" },
    [OrderStatus.ReadyToShip]: { label: OrderStatusLabel[OrderStatus.ReadyToShip], color: "orange", stage: "FULFILMENT" },
    [OrderStatus.Shipped]: { label: OrderStatusLabel[OrderStatus.Shipped], color: "purple", stage: "FULFILMENT" },
    [OrderStatus.OutForDelivery]: { label: OrderStatusLabel[OrderStatus.OutForDelivery], color: "purple", stage: "FULFILMENT" },
    [OrderStatus.Delivered]: { label: OrderStatusLabel[OrderStatus.Delivered], color: "green", stage: "DELIVERED" },
    [OrderStatus.Completed]: { label: OrderStatusLabel[OrderStatus.Completed], color: "green", stage: "DELIVERED" },
    [OrderStatus.Ready]: { label: OrderStatusLabel[OrderStatus.Ready], color: "blue", stage: "CONFIRMED" },
    [OrderStatus.Cancelled]: { label: OrderStatusLabel[OrderStatus.Cancelled], color: "red", stage: "CANCELLED" },
    [OrderStatus.Returned]: { label: OrderStatusLabel[OrderStatus.Returned], color: "red", stage: "CANCELLED" },
    [OrderStatus.Refunded]: { label: OrderStatusLabel[OrderStatus.Refunded], color: "red", stage: "CANCELLED" },
  }

  if (parsed !== OrderStatus.Unknown) {
    return semanticByStatus[parsed]
  }

  const byKey: Record<string, OrderDetailsStatusSemantic> = {
    confirmed: { label: OrderStatusLabel[OrderStatus.Pending], color: "blue", stage: "CONFIRMED" },
    ordercreated: { label: OrderStatusLabel[OrderStatus.Pending], color: "yellow", stage: "PLACED" },
    accepted: { label: OrderStatusLabel[OrderStatus.Confirmed], color: "blue", stage: "CONFIRMED" },
    restaurantaccepted: { label: OrderStatusLabel[OrderStatus.Confirmed], color: "blue", stage: "CONFIRMED" },
    preparing: semanticByStatus[OrderStatus.Preparing],
    packaging: semanticByStatus[OrderStatus.Packaging],
    readytoship: semanticByStatus[OrderStatus.ReadyToShip],
    shipped: semanticByStatus[OrderStatus.Shipped],
    outfordelivery: semanticByStatus[OrderStatus.OutForDelivery],
    delivered: semanticByStatus[OrderStatus.Delivered],
    completed: semanticByStatus[OrderStatus.Completed],
    pending: semanticByStatus[OrderStatus.Pending],
    ready: semanticByStatus[OrderStatus.Ready],
    cancelled: semanticByStatus[OrderStatus.Cancelled],
    returned: semanticByStatus[OrderStatus.Returned],
    refunded: semanticByStatus[OrderStatus.Refunded],
  }

  if (t && byKey[t]) return byKey[t]

  const asNum = parseInt(t, 10)
  if (!Number.isNaN(asNum) && asNum >= OrderStatus.Unknown && asNum <= OrderStatus.Completed) {
    const mapped = semanticByStatus[asNum as OrderStatus]
    if (mapped) return mapped
    return {
      label: OrderStatusLabel[asNum as OrderStatus],
      color: "gray",
      stage: "UNKNOWN",
    }
  }

  if (token) {
    return {
      label: titleCasePaymentType(token.replace(/_/g, " ")),
      color: "gray",
      stage: "UNKNOWN",
    }
  }

  return { label: fb, color: "gray", stage: "UNKNOWN" }
}

/**
 * Maps the customer order-detail envelope `data` (order payload) → {@link OrderDetailsUI}.
 * Source is the tenant-scoped GET /api/public/v1/storefront/orders/{orderId};
 * staff GET /orders/v1/{orderId} is a separate contract.
 * Tolerates legacy flat fields and newer nested shapes (`order_status` object, `modifiers` on lines, etc.).
 */
export function mapOrderDetailsApiData(raw: unknown): OrderDetailsUI {
  if (!isRecord(raw)) {
    throw new Error("Invalid order response")
  }
  const data = raw
  const orderId = str(data.order_id)
  if (!orderId) {
    throw new Error("Invalid order response")
  }

  const orderNumber = Math.max(0, Math.floor(num(data.order_number)))

  const statusBlock = data.order_status
  let statusLabel = ""
  let statusCodeStr = ""
  let orderStatus: OrderStatus = OrderStatus.Unknown

  if (isRecord(statusBlock)) {
    statusLabel = str(statusBlock.name) || str(statusBlock.label) || ""
    statusCodeStr = str(statusBlock.code) || str(statusBlock.value) || str(statusBlock.id)
    orderStatus = parseOrderStatus(statusBlock.code ?? statusBlock.value ?? statusBlock.name ?? statusBlock.id)
  } else {
    orderStatus = parseOrderStatus(data.order_status)
    statusCodeStr = String(orderStatus)
    statusLabel = OrderStatusLabel[orderStatus] ?? "Unknown"
  }
  if (!statusLabel) {
    statusLabel = OrderStatusLabel[orderStatus] ?? "Unknown"
  }
  if (!statusCodeStr) {
    statusCodeStr = String(orderStatus)
  }

  const statusSemantic = mapOrderStatusSemantic(
    isRecord(statusBlock) ? statusBlock : data.order_status,
    statusLabel,
    statusCodeStr
  )

  const paymentTimingBlock = data.payment_timing
  let paymentTimingLabel = ""
  let paymentTimingCodeStr = ""
  let paymentTiming: PaymentTiming = PaymentTiming.Unknown

  if (isRecord(paymentTimingBlock)) {
    paymentTimingLabel = str(paymentTimingBlock.name) || str(paymentTimingBlock.label) || ""
    paymentTimingCodeStr = str(paymentTimingBlock.code) || str(paymentTimingBlock.value) || str(paymentTimingBlock.id)
    paymentTiming = parsePaymentTiming(
      paymentTimingBlock.code ?? paymentTimingBlock.value ?? paymentTimingBlock.name ?? paymentTimingBlock.id
    )
  } else if (data.payment_timing != null) {
    paymentTiming = parsePaymentTiming(data.payment_timing)
    paymentTimingCodeStr = String(paymentTiming)
    paymentTimingLabel = PaymentTimingLabel[paymentTiming] ?? "Unknown"
  }
  if (paymentTiming === PaymentTiming.Unknown) {
    paymentTiming = resolvePaymentTimingFromOrder(data)
    if (paymentTiming !== PaymentTiming.Unknown) {
      paymentTimingLabel = PaymentTimingLabel[paymentTiming] ?? "Unknown"
      paymentTimingCodeStr = paymentTimingToCode(paymentTiming)
    }
  }
  if (!paymentTimingLabel) {
    paymentTimingLabel = PaymentTimingLabel[paymentTiming] ?? "Unknown"
  }
  if (!paymentTimingCodeStr) {
    paymentTimingCodeStr = paymentTiming === PaymentTiming.Unknown ? String(paymentTiming) : paymentTimingToCode(paymentTiming)
  }

  const bill = isRecord(data.order_bill_summary) ? data.order_bill_summary : null
  const hasOrderBillSummary = bill != null

  // Same SavingsSummary contract as billing preview — prefer bill summary, then root payload.
  const savingsSummary = mapSavingsSummary(
    bill?.savings_summary ?? data.savings_summary
  )

  const summary = {
    subTotal: moneyFromUnknown(bill?.subtotal ?? bill?.sub_total ?? data.subtotal ?? 0),
    totalDiscount: moneyFromUnknown(bill?.total_discount ?? data.total_discount ?? 0),
    totalTax: moneyFromUnknown(bill?.total_tax ?? data.total_tax ?? 0),
    totalFees: moneyFromUnknown(bill?.total_fees ?? data.total_fees ?? 0),
    payableAmount: moneyFromUnknown(bill?.payable_amount ?? data.payable_amount ?? 0),
  }

  const currencyCode = str(data.currency_code)
  const currencySymbol = str(data.currency_symbol)
  const restaurantId = str(data.restaurant_id ?? data.restaurantId ?? "")

  const items = pickArray(data.orderDetailsList).map((row) => {
    const it = isRecord(row) ? row : {}
    const qty = Math.max(1, Math.floor(num(it.quantity)) || 1)
    const unitPrice = moneyFromUnknown(it.unit_price ?? it.item_price)
    const referenceRaw = it.reference_unit_price ?? it.referenceUnitPrice
    const referenceUnitPrice =
      referenceRaw == null || referenceRaw === ""
        ? null
        : moneyFromUnknown(referenceRaw)
    const lineTotal = moneyFromUnknown(it.line_total ?? it.lineTotal ?? qty * unitPrice)

    const mods = pickArray(it.modifiers ?? it.modifier_lines ?? it.selected_modifiers).map((m) => {
      const mr = isRecord(m) ? m : {}
      const mq = Math.max(1, Math.floor(num(mr.quantity ?? 1)) || 1)
      const mPrice = moneyFromUnknown(mr.unit_price ?? mr.price ?? mr.modifier_price ?? mr.delta_price)
      const mTotal = moneyFromUnknown(mr.line_total ?? mr.total ?? mq * mPrice)
      const modGid = str(mr.modifier_group_id ?? mr.group_id)
      const modBridge = str(mr.modifier_group_item_id ?? mr.addon_id ?? mr.item_id)
      const modChild = str(mr.child_menu_item_id ?? mr.menu_item_id)
      return {
        name: str(mr.modifier_name ?? mr.name ?? mr.item_name) || "Modifier",
        quantity: mq,
        price: mPrice,
        total: mTotal,
        ...(modGid ? { modifierGroupId: modGid } : {}),
        ...(modBridge ? { modifierGroupItemId: modBridge } : {}),
        ...(modChild ? { childMenuItemId: modChild } : {}),
      }
    })

    return {
      name: str(it.item_name ?? it.name) || "Item",
      quantity: qty,
      unitPrice,
      referenceUnitPrice,
      lineTotal,
      modifiers: mods,
      menuItemId: str(it.menu_item_id ?? it.item_id ?? it.menuItemId ?? ""),
      category: str(it.category ?? it.menu_category ?? it.item_category ?? ""),
    }
  })

  const charges: OrderDetailsCharge[] = pickArray(data.orderChargesList).map((row) => {
    const c = isRecord(row) ? row : {}
    const chargeType = classifyCharge(c)
    return {
      name: str(c.charge_name ?? c.name) || "Charge",
      amount: moneyFromUnknown(c.charge_applied_amount ?? c.applied_amount ?? c.amount ?? 0),
      chargeType,
    }
  })

  const discounts = pickArray(data.orderDiscountList).map((row) => {
    const d = isRecord(row) ? row : {}
    return {
      name: str(d.discount_name ?? d.discount_code ?? d.name) || undefined,
      amount: moneyFromUnknown(d.discount_applied_amount ?? d.discount_amount ?? d.amount ?? 0),
    }
  })

  const pay = isRecord(data.order_payment_details) ? data.order_payment_details : null
  const paymentAmount = pay ? optionalMoney(pay.amount) : null
  const rawPayType = pay ? pay.payment_type ?? pay.paymentType : null
  const paymentMethod = str(
    data.primary_payment_method ?? data.payment_method ?? pay?.primary_payment_method
  )
  const paymentTypeDisplay =
    resolvePaymentMethodDisplayLabel(rawPayType, paymentMethod) ?? ""

  const paymentStatusFromDetails = pay?.payment_status != null ? str(pay.payment_status).toUpperCase() : ""
  const paymentStatus = paymentStatusFromDetails || str(data.payment_status).toUpperCase() || "UNKNOWN"
  const paymentStatusDisplay = mapPaymentStatusUxLabel(paymentStatus)

  const billingSnapshotId = str(bill?.billing_log_id) || null
  const isPaid = Boolean(data.is_paid)
  const payable = summary.payableAmount
  const canRetry = canRetryPayment({
    isPaid,
    payableAmount: payable,
    billingSnapshotId,
    statusStage: statusSemantic.stage,
    paymentStatus,
    paymentTiming,
    paymentMethod,
  })

  // Persisted order type is authoritative (backend OrderType: TakeAway 2 = Pickup, Delivery 3).
  const isPickup = Number(data.order_type_id) === PICKUP_ORDER_TYPE
  const orderTypeLabel = isPickup
    ? "Pickup"
    : resolveOrderFulfillmentLabel(
        data.order_type ?? data.orderType ?? data.fulfillment_type ?? data.fulfillmentType
      )
  const orderChannelLabel = resolveOrderChannelLabel(
    data.order_channel ?? data.orderChannel ?? data.channel
  )
  const businessDate = str(data.business_date ?? data.businessDate ?? "")
  const paymentPaidAt = str(
    pay?.paid_at_utc ??
      pay?.paid_at ??
      pay?.payment_date ??
      pay?.paidAt ??
      data.paid_at_utc ??
      data.paid_at ??
      ""
  )

  return {
    orderId,
    restaurantId,
    orderNumber,
    status: statusLabel,
    statusCode: statusCodeStr,
    orderStatus,
    statusSemantic,
    customerName: str(data.customer_name ?? data.customerName),
    contactNumber: str(data.contact_number),
    address: str(data.delivery_address),
    city: str(data.city),
    country: str(data.country),
    zipCode: str(data.zip_code),
    // Prefer UTC placement timestamp — `created_date` alone was empty on modern payloads.
    createdDate: str(
      data.created_at_utc ?? data.created_date ?? data.createdDate ?? data.created_at ?? ""
    ),
    updatedDate: str(
      data.updated_at_utc ??
        data.updated_date ??
        data.last_updated_at ??
        data.updated_at ??
        ""
    ),
    orderTypeLabel,
    isPickup,
    orderChannelLabel,
    businessDate,
    paymentPaidAt,
    currency: { code: currencyCode, symbol: currencySymbol },
    isPaid,
    paymentStatus,
    paymentTiming,
    paymentTimingCode: paymentTimingCodeStr,
    paymentTimingDisplay: paymentTimingLabel,
    paymentMethod,
    paymentAmount,
    summary,
    items,
    charges,
    discounts,
    savingsSummary,
    hasOrderBillSummary,
    instructions: str(data.instructions) || "None",
    billingSnapshotId,
    paymentId: pay ? str(pay.payment_id ?? pay.paymentId) || null : null,
    paymentTypeDisplay,
    timeline: buildOrderDetailsTimeline(data),
    canRetryPayment: canRetry,
    paymentStatusDisplay,
    cancellationMessage: mapCancellationMessage(orderStatus, data.cancellation),
  }
}

/** Only the backend message is shown; the reason category and any other field are ignored. */
function mapCancellationMessage(orderStatus: OrderStatus, cancellation: unknown): string | null {
  if (orderStatus !== OrderStatus.Cancelled || !isRecord(cancellation)) return null
  return str(cancellation.message) || null
}

export interface OrderDetailsHttpEnvelope {
  resultCode?: number
  success?: boolean
  message?: unknown
  data?: unknown
}

/**
 * Maps full JSON body from `API.get('/orders/v1/:id')` (axios `response.data`).
 */
export function mapOrderDetailsResponse(apiResponse: OrderDetailsHttpEnvelope): OrderDetailsUI {
  if (!isRecord(apiResponse as unknown)) {
    throw new Error("Invalid order response")
  }
  return mapOrderDetailsApiData(apiResponse.data)
}
